import React, { useEffect, useState, useRef } from 'react';
import { useApp } from '../../context/AppContext';
import { Input } from '../../components/ui/input';
import { Plus, Edit2, Trash2, Sun, Moon, Calendar } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const RULE_ICONS = { seasonal: Sun, weekend: Moon, special_day: Calendar };
const RULE_COLORS = {
  seasonal: { bg: '#fef3c7', border: '#fde68a', color: '#92400e' },
  weekend: { bg: '#dbeafe', border: '#bfdbfe', color: '#1e40af' },
  special_day: { bg: '#fce7f3', border: '#fbcfe8', color: '#9d174d' },
};

export default function AdminPricing() {
  const { apiHeaders, language, t, BACKEND_URL } = useApp();
  const [rules, setRules] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editRule, setEditRule] = useState(null);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('seasonal');
  const [showRoomSelect, setShowRoomSelect] = useState(false);
  const roomRef = useRef(null);

  const emptyRule = { name: '', name_en: '', rule_type: 'seasonal', room_id: null, multiplier: 1.2, start_date: '', end_date: '', description: '', is_active: true };
  const [form, setForm] = useState(emptyRule);

  const RULE_TYPES = [
    { key: 'seasonal', label: language === 'tr' ? 'Sezonluk' : 'Seasonal' },
    { key: 'weekend', label: language === 'tr' ? 'Hafta Sonu' : 'Weekend' },
    { key: 'special_day', label: language === 'tr' ? 'Özel Gün' : 'Special Day' },
  ];

  useEffect(() => { fetchData(); }, []);

  useEffect(() => {
    const h = (e) => { if (roomRef.current && !roomRef.current.contains(e.target)) setShowRoomSelect(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  const fetchData = async () => {
    try {
      const [rulesRes, roomsRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/pricing`, apiHeaders()),
        axios.get(`${BACKEND_URL}/api/rooms`),
      ]);
      setRules(rulesRes.data); setRooms(roomsRes.data);
    } catch {}
    setLoading(false);
  };

  const openEdit = (rule) => { setEditRule(rule); setForm({...rule}); setDialogOpen(true); };
  const openAdd = () => { setEditRule(null); setForm({...emptyRule, rule_type: activeTab}); setDialogOpen(true); };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = { ...form, multiplier: parseFloat(form.multiplier) };
      if (!payload.room_id || payload.room_id === 'all') payload.room_id = null;
      if (editRule) { await axios.put(`${BACKEND_URL}/api/pricing/${editRule._id}`, payload, apiHeaders()); toast.success(language === 'tr' ? 'Güncellendi' : 'Updated'); }
      else { await axios.post(`${BACKEND_URL}/api/pricing`, payload, apiHeaders()); toast.success(language === 'tr' ? 'Eklendi' : 'Added'); }
      setDialogOpen(false); fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Error'); }
    setSaving(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm(language === 'tr' ? 'Bu kural silinsin mi?' : 'Delete this rule?')) return;
    try { await axios.delete(`${BACKEND_URL}/api/pricing/${id}`, apiHeaders()); toast.success(language === 'tr' ? 'Silindi' : 'Deleted'); fetchData(); }
    catch { toast.error('Error'); }
  };

  const filteredRules = rules.filter(r => r.rule_type === activeTab);
  const multiplierLabel = (m) => {
    const pct = Math.round((m - 1) * 100);
    return pct >= 0 ? `+${pct}%` : `${pct}%`;
  };

  const S = {
    label: { display: 'block', fontSize: '11px', fontWeight: 700, color: '#6E7F5B', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '5px' },
    input: { border: '1.5px solid #D8D1C6', borderRadius: '8px', fontSize: '14px', fontFamily: 'Manrope, sans-serif', width: '100%', padding: '9px 12px', boxSizing: 'border-box', outline: 'none' },
  };

  return (
    <div style={{ fontFamily: 'Manrope, sans-serif' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '20px', flexWrap: 'wrap', gap: '12px' }}>
        <div>
          <h1 style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: '28px', fontWeight: 600, color: '#1F3A2E', marginBottom: '2px' }}>{t('pricing_rules')}</h1>
          <p style={{ fontSize: '13px', color: '#6E7F5B' }}>{rules.length} {language === 'tr' ? 'aktif kural' : 'active rules'}</p>
        </div>
        <button onClick={openAdd} style={{ display: 'flex', alignItems: 'center', gap: '6px', background: '#1F3A2E', color: 'white', padding: '10px 18px', borderRadius: '10px', border: 'none', cursor: 'pointer', fontSize: '13px', fontWeight: 700, fontFamily: 'Manrope, sans-serif' }}>
          <Plus size={15} /> {t('add_rule')}
        </button>
      </div>

      {/* Info box */}
      <div style={{ background: '#EFE7DA', border: '1px solid #D8D1C6', borderRadius: '12px', padding: '12px 16px', marginBottom: '20px', fontSize: '13px', color: '#3E5A3F' }}>
        <strong>{language === 'tr' ? 'Çarpan Açıklaması:' : 'Multiplier Guide:'}</strong>{' '}
        1.0 = {language === 'tr' ? 'Normal fiyat' : 'Normal price'} · 1.2 = +20% · 1.35 = +35% · 0.85 = -15% · 0.9 = -10%
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: '8px', marginBottom: '20px', flexWrap: 'wrap' }}>
        {RULE_TYPES.map(type => {
          const count = rules.filter(r => r.rule_type === type.key).length;
          const rc = RULE_COLORS[type.key];
          return (
            <button key={type.key} onClick={() => setActiveTab(type.key)}
              style={{ padding: '8px 16px', borderRadius: '20px', fontSize: '13px', fontWeight: 600, cursor: 'pointer', fontFamily: 'Manrope, sans-serif', border: activeTab === type.key ? '2px solid #1F3A2E' : '1.5px solid #D8D1C6', background: activeTab === type.key ? '#1F3A2E' : 'white', color: activeTab === type.key ? 'white' : '#5A5450' }}>
              {type.label} ({count})
            </button>
          );
        })}
      </div>

      {/* Rules List */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: '40px', color: '#6E7F5B' }}>{language === 'tr' ? 'Yükleniyor...' : 'Loading...'}</div>
      ) : filteredRules.length === 0 ? (
        <div style={{ background: 'white', border: '1px solid #EFE7DA', borderRadius: '12px', padding: '40px', textAlign: 'center', color: '#B0A99A' }}>
          <p>{language === 'tr' ? 'Henüz kural yok' : 'No rules yet'}</p>
          <button onClick={openAdd} style={{ marginTop: '12px', background: '#1F3A2E', color: 'white', padding: '8px 16px', borderRadius: '8px', border: 'none', cursor: 'pointer', fontSize: '13px', fontFamily: 'Manrope, sans-serif' }}>
            + {language === 'tr' ? 'Kural Ekle' : 'Add Rule'}
          </button>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          {filteredRules.map(rule => {
            const Icon = RULE_ICONS[rule.rule_type] || Calendar;
            const rc = RULE_COLORS[rule.rule_type] || { bg: '#F6F1E7', border: '#D8D1C6', color: '#1F3A2E' };
            const pct = multiplierLabel(rule.multiplier);
            return (
              <div key={rule._id} style={{ background: 'white', border: '1px solid #EFE7DA', borderRadius: '14px', padding: '16px 20px', display: 'flex', alignItems: 'flex-start', gap: '14px' }}>
                <div style={{ width: '40px', height: '40px', borderRadius: '10px', background: rc.bg, border: `1px solid ${rc.border}`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <Icon size={18} style={{ color: rc.color }} />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap', marginBottom: '4px' }}>
                    <p style={{ fontWeight: 700, fontSize: '14px', color: '#1F3A2E' }}>{language === 'tr' ? rule.name : (rule.name_en || rule.name)}</p>
                    <span style={{ background: parseFloat(rule.multiplier) >= 1 ? '#dcfce7' : '#fef3c7', color: parseFloat(rule.multiplier) >= 1 ? '#166534' : '#92400e', fontSize: '12px', fontWeight: 700, padding: '2px 10px', borderRadius: '20px' }}>
                      {pct}
                    </span>
                    {!rule.room_id && <span style={{ background: '#EFE7DA', color: '#3E5A3F', fontSize: '11px', fontWeight: 600, padding: '2px 8px', borderRadius: '20px' }}>{language === 'tr' ? 'Tüm Odalar' : 'All Rooms'}</span>}
                  </div>
                  {rule.description && <p style={{ fontSize: '13px', color: '#6E7F5B', marginBottom: '4px' }}>{rule.description}</p>}
                  {rule.start_date && rule.end_date && <p style={{ fontSize: '12px', color: '#B0A99A' }}>{rule.start_date} → {rule.end_date}</p>}
                </div>
                <div style={{ display: 'flex', gap: '6px', flexShrink: 0 }}>
                  <button onClick={() => openEdit(rule)} style={{ width: '32px', height: '32px', borderRadius: '8px', border: '1px solid #D8D1C6', background: 'white', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Edit2 size={13} style={{ color: '#6E7F5B' }} />
                  </button>
                  <button onClick={() => handleDelete(rule._id)} style={{ width: '32px', height: '32px', borderRadius: '8px', border: '1px solid #fca5a5', background: '#fff5f5', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Trash2 size={13} style={{ color: '#dc2626' }} />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Add/Edit Modal - Custom overlay */}
      {dialogOpen && (
        <div onClick={(e) => { if (e.target === e.currentTarget) setDialogOpen(false); }}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}>
          <div style={{ background: 'white', borderRadius: '20px', width: '100%', maxWidth: '500px', maxHeight: '85vh', overflowY: 'auto', padding: '24px', fontFamily: 'Manrope, sans-serif' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h2 style={{ fontFamily: 'Cormorant Garamond, serif', color: '#1F3A2E', fontSize: '20px', fontWeight: 600 }}>
                {editRule ? (language === 'tr' ? 'Kuralı Düzenle' : 'Edit Rule') : (language === 'tr' ? 'Yeni Kural' : 'New Rule')}
              </h2>
              <button onClick={() => setDialogOpen(false)} style={{ background: '#F6F1E7', border: 'none', width: '32px', height: '32px', borderRadius: '8px', cursor: 'pointer', fontSize: '16px', color: '#6E7F5B' }}>✕</button>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <div><label style={S.label}>{language === 'tr' ? 'Ad (TR)' : 'Name (TR)'}</label>
                  <input value={form.name} onChange={e => setForm(f => ({...f, name: e.target.value}))} style={S.input} onFocus={e => e.target.style.borderColor = '#1F3A2E'} onBlur={e => e.target.style.borderColor = '#D8D1C6'} /></div>
                <div><label style={S.label}>{language === 'tr' ? 'Ad (EN)' : 'Name (EN)'}</label>
                  <input value={form.name_en || ''} onChange={e => setForm(f => ({...f, name_en: e.target.value}))} style={S.input} onFocus={e => e.target.style.borderColor = '#1F3A2E'} onBlur={e => e.target.style.borderColor = '#D8D1C6'} /></div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <div>
                  <label style={S.label}>{t('rule_type')}</label>
                  <select value={form.rule_type} onChange={e => setForm(f => ({...f, rule_type: e.target.value}))} style={S.input}>
                    {RULE_TYPES.map(rt => <option key={rt.key} value={rt.key}>{rt.label}</option>)}
                  </select>
                </div>
                <div>
                  <label style={S.label}>{t('multiplier')} (1.2 = +20%)</label>
                  <input type="number" step="0.05" min="0.1" max="5" value={form.multiplier} onChange={e => setForm(f => ({...f, multiplier: e.target.value}))} style={S.input} onFocus={e => e.target.style.borderColor = '#1F3A2E'} onBlur={e => e.target.style.borderColor = '#D8D1C6'} />
                </div>
              </div>
              {form.rule_type !== 'weekend' && (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                  <div><label style={S.label}>{t('start_date')}</label>
                    <input type="date" value={form.start_date || ''} onChange={e => setForm(f => ({...f, start_date: e.target.value}))} style={S.input} /></div>
                  <div><label style={S.label}>{t('end_date')}</label>
                    <input type="date" value={form.end_date || ''} onChange={e => setForm(f => ({...f, end_date: e.target.value}))} style={S.input} /></div>
                </div>
              )}
              <div>
                <label style={S.label}>{language === 'tr' ? 'Oda (boş = tüm odalar)' : 'Room (empty = all rooms)'}</label>
                <select value={form.room_id || 'all'} onChange={e => setForm(f => ({...f, room_id: e.target.value === 'all' ? null : e.target.value}))} style={S.input}>
                  <option value="all">{language === 'tr' ? 'Tüm Odalar' : 'All Rooms'}</option>
                  {rooms.map(r => <option key={r._id} value={r._id}>{r.name}</option>)}
                </select>
              </div>
              <div>
                <label style={S.label}>{language === 'tr' ? 'Açıklama' : 'Description'}</label>
                <input value={form.description || ''} onChange={e => setForm(f => ({...f, description: e.target.value}))} placeholder={language === 'tr' ? 'Kural açıklaması...' : 'Rule description...'} style={S.input} onFocus={e => e.target.style.borderColor = '#1F3A2E'} onBlur={e => e.target.style.borderColor = '#D8D1C6'} />
              </div>
              <div style={{ display: 'flex', gap: '10px', paddingTop: '4px' }}>
                <button onClick={handleSave} disabled={saving} style={{ flex: 1, background: '#1F3A2E', color: 'white', padding: '13px', borderRadius: '10px', border: 'none', cursor: 'pointer', fontSize: '14px', fontWeight: 700, fontFamily: 'Manrope, sans-serif' }}>
                  {saving ? '...' : t('save')}
                </button>
                <button onClick={() => setDialogOpen(false)} style={{ padding: '13px 18px', background: 'white', border: '1px solid #D8D1C6', borderRadius: '10px', cursor: 'pointer', fontSize: '14px', fontFamily: 'Manrope, sans-serif' }}>
                  {language === 'tr' ? 'İptal' : 'Cancel'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
